/**
 * @fileoverview Route-level permission guard.
 *
 * แก้ปัญหา: user ที่ไม่มี permission ของเมนู ยังสามารถพิมพ์ URL เข้าหน้าได้โดยตรง
 * (เมนูถูกซ่อนแล้วจาก Navbar แต่ route ไม่ได้ตรวจสิทธิ์)
 *
 * วิธีการ: ใช้ "แหล่งความจริงเดียว" (single source of truth) คือ map ของ
 * link -> permission ที่ประกาศไว้แล้วใน global.js (global.menu_admin/customer/
 * warehouse/super_user) มาตรวจสิทธิ์ตอนเข้าถึง route โดยตรง
 * หน้าที่ไม่ได้อยู่ในเมนู (เช่นฟอร์มเพิ่ม/แก้ทรัพย์) ประกาศไว้ที่ global.route_permission
 *
 * กันแบบ "ไม่ประกาศ = ไม่ให้เข้า" — เพิ่มหน้าใหม่แล้วลืมประกาศสิทธิ์จะเข้าไม่ได้ทันที
 * ดีกว่าปล่อยผ่านเงียบ ๆ แล้วกลายเป็นช่องโหว่ที่ไม่มีใครรู้
 *
 * ใช้เป็น layout route ของ react-router v6:
 *   <Route element={<PermissionRoute />}>
 *     <Route path="/ad/dashboard" element={<AdminDashboard />} />
 *     ...
 *   </Route>
 */

import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
// ensure global.menu_* are populated (idempotent import)
import "../global.js";

/**
 * สร้าง map ของ pathname -> permission ที่ต้องใช้ จากนิยามเมนูใน global.js
 * @returns {Object<string,string>}
 */
const buildPermissionMap = () => {
  const map = {};
  const menus = [
    ...(global.menu_admin || []),
    ...(global.menu_customer || []),
    ...(global.menu_warehouse || []),
    ...(global.menu_super_user || []),
  ];
  // เมนูกลุ่ม (มี children) เก็บ link ไว้ที่เมนูลูก จึงต้องคลี่ออกมาด้วย
  // ไม่งั้นหน้าที่อยู่ใต้กลุ่ม เช่น /ad/reports/property จะไม่ถูกตรวจสิทธิ์เลย
  const flat = menus.flatMap((item) => (item.children ? item.children : [item]));
  flat.forEach((item) => {
    if (item.link && item.permission) {
      const path = item.link.split("?")[0]; // ตัด query string ออก
      map[path] = item.permission;
    }
  });
  return map;
};

/**
 * เทียบ path กับ pattern ที่มีส่วนแปรผัน — ":uid" ตรงกับส่วนใดก็ได้ที่ไม่ว่าง
 * @param {string} pattern เช่น /ad/assets/:uid/edit
 * @param {string} pathname
 * @returns {boolean}
 */
const matchPattern = (pattern, pathname) => {
  const a = pattern.split("/");
  const b = pathname.split("/");
  if (a.length !== b.length) return false;
  return a.every((part, i) => (part.startsWith(":") ? !!b[i] : part === b[i]));
};

/**
 * หา permission ที่หน้านี้ต้องใช้
 * ลำดับ : ตรงตัวจากเมนู > ที่ประกาศไว้ใน route_permission > เมนูแม่ที่เป็น prefix
 *
 * ขั้น prefix รองรับหน้าย่อยที่ยังไม่ได้ประกาศ (เช่น /ad/assets/xxx ในอนาคต)
 * ให้ตกไปใช้สิทธิ์ของหน้าแม่แทนที่จะหลุดเป็นหน้าไม่มีการตรวจ
 * @param {string} pathname
 * @returns {string|null} null = ไม่มีที่ไหนประกาศไว้เลย
 */
const requiredPermission = (pathname) => {
  const map = buildPermissionMap();
  if (map[pathname]) return map[pathname];

  const declared = (global.route_permission || []).find((route) => matchPattern(route.path, pathname));
  if (declared) return declared.permission;

  // เลือก prefix ที่ยาวที่สุด เพราะ /ad/marketing/channels ต้องได้สิทธิ์ของตัวเอง
  // ไม่ใช่ของ /ad/marketing ที่เป็น prefix สั้นกว่า
  const parent = Object.keys(map)
    .filter((link) => pathname.startsWith(`${link}/`))
    .sort((x, y) => y.length - x.length)[0];

  return parent ? map[parent] : null;
};

/**
 * อ่าน profile จาก localStorage (ค่าล่าสุดเสมอ)
 * @returns {Object|null}
 */
const getProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("mn-profile"));
  } catch {
    return null;
  }
};

const PermissionRoute = () => {
  const location = useLocation();
  const token = localStorage.getItem("mn-acc-tk");
  const profile = getProfile();

  // 1) ยังไม่ได้ login -> ไปหน้า login
  if (!token || !profile) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // 2) บัญชีระดับแพลตฟอร์ม/บริษัท ข้ามการตรวจสิทธิ์ระดับหน้า
  //    (สอดคล้องกับ checkPermission ฝั่ง backend ที่ปล่อยผ่าน super_user เช่นกัน)
  if (["super_user"].includes(profile.user_type)) {
    return <Outlet />;
  }

  // 3) ตรวจสิทธิ์จาก permission ของ role
  const required = requiredPermission(location.pathname);
  const perms = profile?.role?.permission || [];
  if (required && perms.includes(required)) {
    return <Outlet />;
  }

  // 4) ไม่มีสิทธิ์ หรือหน้านี้ไม่มีใครประกาศสิทธิ์ไว้ -> ส่งไปหน้า not found (catch-all route)
  return <Navigate to="/notfound" replace />;
};

export default PermissionRoute;
